import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import FormTextField from '../../../../../antd/form/FormTextField/FormTextField.jsx';
import { required } from '../../../../../antd/form/FormValidation.js';
import FormTextEditor from '../../../../../antd/form/FormTextEditor/FormTextEditor.jsx';
import FormButton from '../../../../../antd/form/FormButton/FormButton.jsx';
import FormButtonsContainer from '../../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import Form from '../../../../../antd/form/Form/Form.jsx';
import FormSelect from '../../../../../antd/form/FormSelect/FormSelect.jsx';
import { Alert, Col, Form as AntdForm, Row } from 'antd';
import FormDate from '../../../../../antd/form/FormDate/FormDate.jsx';
import dayjs from 'dayjs';
import {
  DATETIME_FORMAT_DATE,
  DATETIME_FORMAT_DATETIME,
} from '../../../../../../utils/helpers/CommonHelper.jsx';
import FormItem from 'antd/es/form/FormItem/index';

const FoDatesForm = ({
  onSubmit,
  initials,
  teachers,
  form,
  collidedDates,
  eventDateFrom,
  eventDateTo,
}) => {
  const { t } = useTranslation();
  const { getFieldValue } = form;
  const eventStartBoundary = eventDateFrom
    ? dayjs(eventDateFrom).startOf('day')
    : null;
  const eventEndBoundary = eventDateTo ? dayjs(eventDateTo).endOf('day') : null;

  useEffect(() => {
    if (initials?.lecturer_id && !form.getFieldValue('lecturer_id')) {
      form.setFieldValue('lecturer_id', String(initials.lecturer_id));
    }
  }, [initials?.lecturer_id, form]);

  const handleSubmit = (values) => {
    onSubmit(values);
  };

  const watchFrom = AntdForm.useWatch('date_from', form);
  const disabledDate = (current) => {
    if (!current) {
      return false;
    }
    if (eventStartBoundary && current.isBefore(eventStartBoundary, 'day')) {
      return true;
    }
    if (eventEndBoundary && current.isAfter(eventEndBoundary, 'day')) {
      return true;
    }
    return current.isSameOrBefore(dayjs(), 'day');
  };
  const disabledDateTo = (current) => {
    if (!current) {
      return false;
    }
    if (eventStartBoundary && current.isBefore(eventStartBoundary, 'day')) {
      return true;
    }
    if (eventEndBoundary && current.isAfter(eventEndBoundary, 'day')) {
      return true;
    }
    if (!watchFrom) {
      return current.isBefore(dayjs(), 'day');
    }
    return watchFrom.isAfter(current, 'day');
  };
  const renderCollidedDates = (collidedDates) => {
    const dates = collidedDates?.collided_dates || [];

    if (getFieldValue('date_from') === undefined) {
      return (
        <Alert
          title={t(
            'Ak vyplníte pole do, aplikácia zistí ostatné termíny v rovnaký čas a zobrazí kolízie.',
          )}
          type="info"
          showIcon
          style={{ marginBottom: '10px' }}
        />
      );
    } else if (dates.length > 0) {
      return (
        <Alert
          type={'warning'}
          title={t('Zoznam termínov, ktoré sú v rovnakom čase') + ':'}
          description={
            <ul>
              {dates.map((date) => {
                if (
                  date.date_from !== form.getFieldValue('date_from') ||
                  date.date_to !== form.getFieldValue('date_to') ||
                  date.location !== form.getFieldValue('location')
                ) {
                  return (
                    <li key={date.id}>
                      {t('Seminár')}: <b>{date.workshop_name}</b>
                      {' – '}
                      {date.name}
                      {', '}
                      {dayjs(date.date_from).format('D.M.YYYY H:mm')}–
                      {dayjs(date.date_to).format('H:mm')}
                      {date.location ? ` (${date.location})` : ''}
                    </li>
                  );
                }
              })}
            </ul>
          }
        />
      );
    } else {
      return (
        <Alert
          title={t('Nie sú žiadne kolidujúce termíny.')}
          type="success"
          showIcon
          style={{ marginBottom: '10px' }}
        />
      );
    }
  };

  return (
    <Form form={form} onFinish={handleSubmit} initialValues={initials}>
      <FormTextField label={t('Názov')} name={'name'} rules={[required(t)]} />
      <FormSelect
        label={t('Lektor')}
        name={'lecturer_id'}
        initialValue={initials?.lecturer_id}
        options={teachers}
        placeholder={t('Vyhľadať lektora')}
        rules={[required(t)]}
      />
      <FormTextField
        label={t('Miestnosť')}
        name={'location'}
        rules={[required(t)]}
      />
      <FormItem>
        <Row gutter={[10]} style={{ width: '100%' }}>
          <Col span={5}>
            <FormDate
              defaultValue={dayjs(getFieldValue('date_from'))}
              label={t('Začiatok termínu')}
              placeholder={dayjs().format(DATETIME_FORMAT_DATETIME)}
              name={'date_from'}
              rules={[
                {
                  validator: async (_, date_from) => {
                    if (
                      !date_from ||
                      !eventStartBoundary ||
                      !eventEndBoundary
                    ) {
                      return Promise.resolve();
                    }

                    if (
                      date_from.isBefore(eventStartBoundary) ||
                      date_from.isAfter(eventEndBoundary)
                    ) {
                      return Promise.reject(
                        t(
                          'Začiatok termínu musí byť v rozsahu konania akcie (od {{from}} do {{to}}).',
                          {
                            from: eventStartBoundary.format(
                              DATETIME_FORMAT_DATE,
                            ),
                            to: eventEndBoundary.format(DATETIME_FORMAT_DATE),
                          },
                        ),
                      );
                    }

                    return Promise.resolve();
                  },
                },
                required(t),
              ]}
              showTime={true}
              format={DATETIME_FORMAT_DATETIME}
              disabledDate={disabledDate}
            />
          </Col>
          <Col span={5}>
            <FormDate
              defaultValue={dayjs(getFieldValue('date_to'))}
              label={t('Koniec termínu')}
              placeholder={dayjs()
                .add(1, 'hour')
                .format(DATETIME_FORMAT_DATETIME)}
              name={'date_to'}
              rules={[
                {
                  validator: async (_, date_to) => {
                    const date_from = getFieldValue('date_from');
                    if (date_to != null && date_from >= date_to) {
                      return Promise.reject(
                        t('Koniec musí byť neskôr ako začiatok'),
                      );
                    }

                    if (!date_to || !eventStartBoundary || !eventEndBoundary) {
                      return Promise.resolve();
                    }

                    if (
                      date_to.isBefore(eventStartBoundary) ||
                      date_to.isAfter(eventEndBoundary)
                    ) {
                      return Promise.reject(
                        t(
                          'Koniec termínu musí byť v rozsahu konania akcie (od {{from}} do {{to}}).',
                          {
                            from: eventStartBoundary.format(
                              DATETIME_FORMAT_DATE,
                            ),
                            to: eventEndBoundary.format(DATETIME_FORMAT_DATE),
                          },
                        ),
                      );
                    }

                    return Promise.resolve();
                  },
                },
                required(t),
              ]}
              showTime={true}
              disabledDate={disabledDateTo}
              format={DATETIME_FORMAT_DATETIME}
            />
          </Col>
        </Row>
        {eventDateFrom && eventDateTo ? (
          <Alert
            title={
              t('Akcia prebieha od') +
              ' ' +
              dayjs(eventDateFrom).format(DATETIME_FORMAT_DATE) +
              ' ' +
              t('do') +
              ' ' +
              dayjs(eventDateTo).format(DATETIME_FORMAT_DATE)
            }
            type="info"
            showIcon
            style={{ marginTop: '10px', marginBottom: '10px' }}
          />
        ) : null}
        {renderCollidedDates(collidedDates)}
      </FormItem>
      <FormTextField
        label={t('Kapacita')}
        name={'capacity'}
        rules={[required(t)]}
      />
      <FormTextEditor label={t('Poznámka')} name={'note'} />
      <FormButtonsContainer>
        <FormButton submitOnClick={true}>{t('Uložiť')}</FormButton>
      </FormButtonsContainer>
    </Form>
  );
};

export default FoDatesForm;
